import { useMemo, useState } from 'react';
import { ArrowDownUp, ArrowUpRight, ChevronLeft, ChevronRight, MapPin } from 'lucide-react';
import { Link } from 'react-router';
import { jobHref, jobPoint, money, stageLabel, type Job } from './atlasModel';
import { COHORT_PRESENTATION } from '../projectMapCohorts';
export function AtlasRegister({
  jobs,
  selectedId,
  onSelect,
  full,
  nearby,
  radius,
  onExport,
  canExport,
}: {
  jobs: Job[];
  selectedId: string | null;
  onSelect: (job: Job) => void;
  full: boolean;
  nearby: boolean;
  radius: number;
  onExport: () => void;
  canExport: boolean;
}) {
  const [sort, setSort] = useState<'name' | 'stage' | 'value'>('name');
  const [descending, setDescending] = useState(false);
  const [page, setPage] = useState(0);
  const count = full ? 50 : 15;
  const sorted = useMemo(
    () =>
      [...jobs].sort((a, b) => {
        const order =
          sort === 'value'
            ? Number(a.financials?.contractValue ?? -1) - Number(b.financials?.contractValue ?? -1)
            : sort === 'stage'
              ? stageLabel(a.cohort).localeCompare(stageLabel(b.cohort))
              : a.name.localeCompare(b.name);
        return order * (descending ? -1 : 1);
      }),
    [jobs, sort, descending],
  );
  const lastPage = Math.max(0, Math.ceil(jobs.length / count) - 1);
  const currentPage = Math.min(page, lastPage);
  return (
    <section
      className={'atlas-register ' + (full ? 'atlas-register--full' : '')}
      aria-label="Job register"
    >
      <div className="atlas-register-heading">
        <h2>
          {nearby ? 'Jobs in this area' : 'Job register'}{' '}
          <span>
            {jobs.length} {nearby ? 'within ' + radius + ' mi' : 'matching records'}
          </span>
        </h2>
        <div>
          <select
            aria-label="Sort jobs"
            value={sort}
            onChange={(e) => {
              setSort(e.target.value as typeof sort);
              setPage(0);
            }}
          >
            <option value="name">Project name</option>
            <option value="stage">Job stage</option>
            <option value="value">Contract value</option>
          </select>
          <button
            type="button"
            aria-label={descending ? 'Sort ascending' : 'Sort descending'}
            onClick={() => setDescending(!descending)}
          >
            <ArrowDownUp size={13} />
          </button>
          {canExport ? (
            <button type="button" className="atlas-text-button" onClick={onExport}>
              Export CSV
            </button>
          ) : null}
        </div>
      </div>
      <div className="atlas-table-scroll">
        <table>
          <thead>
            <tr>
              <th>Project</th>
              <th>Stage</th>
              <th>Client</th>
              {full ? (
                <>
                  <th>Location</th>
                  <th>Sector</th>
                  <th>Risk</th>
                </>
              ) : null}
              <th className="atlas-numeric">Value</th>
              <th>
                <span className="sr-only">Source record</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {sorted.slice(currentPage * count, (currentPage + 1) * count).map((job) => (
              <tr key={job.id} data-selected={selectedId === job.id}>
                <td>
                  <button type="button" onClick={() => onSelect(job)} className="atlas-job-button">
                    <MapPin size={12} className={jobPoint(job) ? 'atlas-gold' : 'atlas-muted'} />
                    <span>
                      {job.name}
                      {full ? (
                        <small>
                          {job.projectNumber}
                          {!jobPoint(job) ? ' · Needs coordinates' : ''}
                        </small>
                      ) : null}
                    </span>
                  </button>
                </td>
                <td>
                  <span
                    className="atlas-stage-dot"
                    style={{ background: COHORT_PRESENTATION[job.cohort].color }}
                  />
                  {stageLabel(job.cohort)}
                </td>
                <td>{job.clientName ?? 'Unrecorded'}</td>
                {full ? (
                  <>
                    <td>
                      {[job.location.city, job.location.state].filter(Boolean).join(', ') ||
                        'Unlocated'}
                    </td>
                    <td>{job.sector ?? 'Unrecorded'}</td>
                    <td>{job.riskLevel ?? 'Unrecorded'}</td>
                  </>
                ) : null}
                <td className="atlas-numeric">{money(job.financials?.contractValue)}</td>
                <td>
                  <Link aria-label={'Open source record for ' + job.name} to={jobHref(job)}>
                    <ArrowUpRight size={14} />
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!jobs.length ? (
          <div className="atlas-empty">
            <strong>No matching records</strong>
            <p>
              Adjust filters or turn off the radius filter. Unlocated jobs remain in the full
              register.
            </p>
          </div>
        ) : null}
      </div>
      <div className="atlas-register-footer">
        <span>
          {jobs.length ? currentPage * count + 1 : 0}–
          {Math.min((currentPage + 1) * count, jobs.length)} of {jobs.length}
        </span>
        <span>
          <button
            type="button"
            aria-label="Previous jobs"
            disabled={currentPage === 0}
            onClick={() => setPage(currentPage - 1)}
          >
            <ChevronLeft size={14} />
          </button>
          {currentPage + 1} / {lastPage + 1}
          <button
            type="button"
            aria-label="Next jobs"
            disabled={currentPage >= lastPage}
            onClick={() => setPage(currentPage + 1)}
          >
            <ChevronRight size={14} />
          </button>
        </span>
      </div>
    </section>
  );
}
